import React, { useCallback, useContext, useMemo } from 'react';

import CompoundMetricsContext from 'contexts/CompoundMetricsContext';
import MetricCacheContext from 'contexts/MetricCacheContext';
import MetricsContext from 'contexts/MetricsContext';
import isDefined from 'isDefined';

const buildCompoundMetricsLookup = ({
  allCompoundMetrics,
  allMetrics,
}: {
  allCompoundMetrics: Metrics.CompoundMetric[];
  allMetrics: Metrics.NormalMetric[];
}) => {
  const newLookup = {} as {
    [compoundMetricId: string]: Metrics.NormalMetric[] | undefined;
  };
  allCompoundMetrics.forEach((compoundMetric) => {
    const inputMetrics = compoundMetric.metricIds
      .map((mid) => allMetrics.find((m) => m.id === mid))
      .filter(isDefined);

    newLookup[compoundMetric.id] = inputMetrics;
  });

  return newLookup;
};

const CompoundMetricsProvider = ({
  children,
}: {
  children: JSX.Element | JSX.Element[];
}) => {
  const { compoundMetricsCache } = useContext(MetricCacheContext);
  const { allMetrics } = useContext(MetricsContext);

  const allCompoundMetrics = useMemo(
    () => Array.from(compoundMetricsCache.values()),
    [compoundMetricsCache],
  );

  const compoundMetricInputsLookup = useMemo(
    () => buildCompoundMetricsLookup({ allCompoundMetrics, allMetrics }),
    [allCompoundMetrics, allMetrics],
  );

  const getCompoundMetricWithMetricDefs = useCallback(
    (
      compoundMetricId: string,
    ): Metrics.CompoundMetricWithMetricDefs | undefined => {
      const cm = allCompoundMetrics.find((m) => m.id === compoundMetricId);
      const l = compoundMetricInputsLookup[compoundMetricId];
      if (!l || !cm) {
        return undefined;
      }

      return {
        id: compoundMetricId,
        name: cm.name,
        metrics: l,
        expression: cm.expression,
      };
    },
    [allCompoundMetrics, compoundMetricInputsLookup],
  );

  return (
    <CompoundMetricsContext.Provider
      value={{
        allCompoundMetrics,
        compoundMetricInputsLookup,
        getCompoundMetricWithMetricDefs,
        isLoading: false,
      }}
    >
      {children}
    </CompoundMetricsContext.Provider>
  );
};

export default CompoundMetricsProvider;
